/**
 * Determines how a node should be rendered by an external renderer.
 *
 * @see {@link https://knyt.dev/s/render-modes}
 *
 * @internal scope: workspace
 */
export var RenderMode;
(function (RenderMode) {
    /**
     * An opaque parent is an element that fully controls the
     * rendering of its children, and an external renderer
     * shouldn't change its contents.
     *
     * @public
     */
    RenderMode["Opaque"] = "opaque";
    /**
     * A transparent parent is an element that allows its
     * children to be rendered by an external renderer.
     *
     * This is the default render mode.
     *
     * @public
     */
    RenderMode["Transparent"] = "transparent";
})(RenderMode || (RenderMode = {}));
/**
 * A key used to mark a node's render mode.
 */
/*
 * ### Private Remarks
 *
 * This is a string instead of a symbol to avoid issues with running
 * multiple versions of the library in the same context.
 */
const __renderMode = Symbol.for("knyt.weaver.renderMode");
/**
 * Sets the render mode of the given node.
 *
 * @see {@link https://knyt.dev/s/render-modes}
 *
 * @internal scope: workspace
 */
export function setRenderMode(node, mode) {
    node[__renderMode] = mode;
}
/**
 * Gets the render mode of the given node.
 *
 * @see {@link https://knyt.dev/s/render-modes}
 *
 * @internal scope: workspace
 */
export function getRenderMode(node) {
    return node[__renderMode];
}
function checkRenderMode(node, mode) {
    return getRenderMode(node) === mode;
}
/**
 * Marks the given node as an opaque parent.
 *
 * @see {@link https://knyt.dev/s/render-modes}
 *
 * @internal scope: workspace
 */
export function markNodeAsOpaque(node) {
    setRenderMode(node, RenderMode.Opaque);
}
/**
 * Determines if the given node is an opaque parent.
 *
 * @see {@link https://knyt.dev/s/render-modes}
 *
 * @internal scope: workspace
 */
export function isNodeOpaque(node) {
    return checkRenderMode(node, RenderMode.Opaque);
}
/**
 * Marks the given node as a transparent parent.
 *
 * @see {@link https://knyt.dev/s/render-modes}
 *
 * @internal scope: workspace
 */
export function isNodeTransparent(node) {
    return checkRenderMode(node, RenderMode.Transparent);
}
/**
 * The list of all render modes.
 */
const renderModes = Object.values(RenderMode);
/**
 * Determines if the given value is a valid render mode.
 *
 * @internal scope: workspace
 */
export function isRenderMode(value) {
    return typeof value === "string" && renderModes.includes(value);
}
